import { getTreeStructure } from '../treeStructure';

const SEARCH_RESULT_LIMIT = 10;
const MATCH_TYPE_PRIORITY = {
  page: 0,
  group: 1,
  name: 2,
  option: 3,
  amt: 4,
};
const MATCH_QUALITY_PRIORITY = {
  contains: 0,
  prefix: 1,
  exact: 2,
};

const buildTreeSearchIndex = (tree) => {
  const fieldToContext = {};
  const groupToPages = {};
  const pageEntries = [];

  const walkNode = (node, page, group = null) => {
    if (!Array.isArray(node)) {
      return;
    }

    node.forEach((item) => {
      if (typeof item === 'string') {
        if (fieldToContext[item] === undefined) {
          fieldToContext[item] = { page, group };
        }
        return;
      }

      if (typeof item === 'object' && item !== null) {
        Object.entries(item).forEach(([groupName, childNode]) => {
          if (!groupToPages[groupName]) {
            groupToPages[groupName] = [];
          }
          if (!groupToPages[groupName].includes(page)) {
            groupToPages[groupName].push(page);
          }
          walkNode(childNode, page, groupName);
        });
      }
    });
  };

  Object.values(tree).forEach((section) => {
    Object.entries(section).forEach(([page, node]) => {
      pageEntries.push(page);
      walkNode(node, page);
    });
  });

  return { fieldToContext, groupToPages, pageEntries };
};

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const unique = (values) => [...new Set(values)];

const buildOptionIndex = (options) => {
  const byField = new Map();

  (Array.isArray(options) ? options : []).forEach((option) => {
    const field = option?.field ? String(option.field) : '';
    if (!field) {
      return;
    }

    const name =
      option?.name !== undefined && option?.name !== null
        ? String(option.name)
        : field;

    const values = (Array.isArray(option.options) ? option.options : []).map(
      (value) => String(value)
    );

    const displayValueByNorm = new Map();
    values.forEach((value) => {
      const valueNorm = normalizeText(value);
      if (!valueNorm || displayValueByNorm.has(valueNorm)) {
        return;
      }
      displayValueByNorm.set(valueNorm, value);
    });

    const valueSet = new Set(displayValueByNorm.keys());

    byField.set(field, {
      name,
      nameNorm: normalizeText(name),
      valueSet,
      displayValueByNorm,
    });
  });

  return { byField };
};

const getMatchQuality = (queryNorm, candidateText) => {
  const textNorm = normalizeText(candidateText);
  if (!textNorm || !queryNorm) {
    return null;
  }
  if (textNorm === queryNorm) {
    return 'exact';
  }
  if (textNorm.startsWith(queryNorm)) {
    return 'prefix';
  }
  if (textNorm.includes(queryNorm)) {
    return 'contains';
  }
  return null;
};

const buildBaseCandidates = (queryNorm, treeIndex, optionIndex, amtIndex) => {
  const candidates = [];

  treeIndex.pageEntries.forEach((page) => {
    const matchQuality = getMatchQuality(queryNorm, page);
    if (!matchQuality) {
      return;
    }
    candidates.push({
      searchType: 'page',
      page,
      group: null,
      name: null,
      option: null,
      amt: null,
      matchQuality,
    });
  });

  Object.entries(treeIndex.groupToPages).forEach(([group, pages]) => {
    const matchQuality = getMatchQuality(queryNorm, group);
    if (!matchQuality) {
      return;
    }
    pages.forEach((page) => {
      candidates.push({
        searchType: 'group',
        page,
        group,
        name: null,
        option: null,
        amt: null,
        matchQuality,
      });
    });
  });

  optionIndex.byField.forEach((meta, field) => {
    const context = treeIndex.fieldToContext[field] || {
      page: null,
      group: null,
    };

    const nameQuality = getMatchQuality(queryNorm, meta.name);
    if (nameQuality) {
      if (meta.displayValueByNorm.size === 0) {
        candidates.push({
          searchType: 'name',
          page: context.page,
          group: context.group,
          name: meta.name,
          option: null,
          amt: null,
          matchQuality: nameQuality,
        });
      } else {
        meta.displayValueByNorm.forEach((optionValue) => {
          candidates.push({
            searchType: 'name',
            page: context.page,
            group: context.group,
            name: meta.name,
            option: optionValue,
            amt: null,
            matchQuality: nameQuality,
          });
        });
      }
    }

    meta.displayValueByNorm.forEach((optionValue) => {
      const optionQuality = getMatchQuality(queryNorm, optionValue);
      if (!optionQuality) {
        return;
      }
      candidates.push({
        searchType: 'option',
        page: context.page,
        group: context.group,
        name: meta.name,
        option: optionValue,
        amt: null,
        matchQuality: optionQuality,
      });
    });
  });

  const amtEntries =
    amtIndex && typeof amtIndex === 'object' ? Object.entries(amtIndex) : [];

  amtEntries.forEach(([amtId, rows]) => {
    if (!Array.isArray(rows)) {
      return;
    }

    rows.forEach((row) => {
      const field = row?.field ? String(row.field) : '';
      if (!field) {
        return;
      }
      const context = treeIndex.fieldToContext[field] || {
        page: null,
        group: null,
      };
      const meta = optionIndex.byField.get(field);
      const name =
        row?.name !== undefined && row?.name !== null
          ? String(row.name)
          : meta?.name || field;
      const option =
        row?.value !== undefined &&
        row?.value !== null &&
        String(row.value) !== ''
          ? String(row.value)
          : null;

      const amtQuality = getMatchQuality(queryNorm, String(amtId));
      if (amtQuality) {
        candidates.push({
          searchType: 'amt',
          page: context.page,
          group: context.group,
          name,
          option,
          amt: String(amtId),
          matchQuality: amtQuality,
        });
      }

      const nameQuality = getMatchQuality(queryNorm, name);
      if (nameQuality) {
        candidates.push({
          searchType: 'name',
          page: context.page,
          group: context.group,
          name,
          option,
          amt: String(amtId),
          matchQuality: nameQuality,
        });
      }

      if (option !== null) {
        const optionQuality = getMatchQuality(queryNorm, option);
        if (optionQuality) {
          candidates.push({
            searchType: 'option',
            page: context.page,
            group: context.group,
            name,
            option,
            amt: String(amtId),
            matchQuality: optionQuality,
          });
        }
      }
    });
  });

  return candidates;
};

const isAmtAvailable = (amtRows, optionIndex) => {
  if (!Array.isArray(amtRows)) {
    return false;
  }
  for (const row of amtRows) {
    const field = row?.field ? String(row.field) : '';
    if (!field || !optionIndex.byField.has(field)) {
      return false;
    }
    if (row?.value === null || row?.value === undefined || row?.value === '') {
      continue;
    }
    const normalizedValue = normalizeText(row.value);
    if (!optionIndex.byField.get(field).valueSet.has(normalizedValue)) {
      return false;
    }
  }
  return true;
};

const isNameAvailable = (name, optionIndex) => {
  const nameNorm = normalizeText(name);
  if (!nameNorm) {
    return false;
  }
  for (const meta of optionIndex.byField.values()) {
    if (meta.nameNorm === nameNorm) {
      return true;
    }
  }
  return false;
};

const isOptionAvailable = (name, optionValue, optionIndex) => {
  const nameNorm = normalizeText(name);
  const optionNorm = normalizeText(optionValue);
  if (!nameNorm || !optionNorm) {
    return false;
  }
  for (const meta of optionIndex.byField.values()) {
    if (meta.nameNorm !== nameNorm) {
      continue;
    }
    if (meta.valueSet.has(optionNorm)) {
      return true;
    }
  }
  return false;
};

const resolveCandidateAvailability = (candidates, optionIndex, amtIndex) => {
  return candidates.map((candidate) => {
    let available;

    if (candidate.searchType === 'page' || candidate.searchType === 'group') {
      available = true;
    } else if (candidate.option !== null && candidate.option !== undefined) {
      available = isOptionAvailable(
        candidate.name,
        candidate.option,
        optionIndex
      );
    } else {
      available = isNameAvailable(candidate.name, optionIndex);
    }

    let amt = candidate.amt;
    if (amt !== null && amt !== undefined) {
      const rows = amtIndex?.[amt] ?? null;
      const amtValid = isAmtAvailable(rows, optionIndex);
      if (available !== amtValid) {
        amt = null;
      }
    }

    return {
      ...candidate,
      amt,
      available,
    };
  });
};

const aggregateCandidates = (candidates) => {
  const UNPAIRED_OPTION_KEY = '__UNPAIRED__';
  const byKey = new Map();

  candidates.forEach((candidate) => {
    const key = [
      candidate.searchType,
      candidate.page ?? '',
      candidate.group ?? '',
      candidate.name ?? '',
      String(Boolean(candidate.available)),
    ].join('::');

    if (!byKey.has(key)) {
      byKey.set(key, {
        searchType: candidate.searchType,
        page: candidate.page ?? null,
        group: candidate.group ?? null,
        name: candidate.name ?? null,
        available: Boolean(candidate.available),
        optionAmtPairs: [],
        _pairs: new Map(),
        _bestQuality: candidate.matchQuality,
      });
    }

    const agg = byKey.get(key);
    if (
      MATCH_QUALITY_PRIORITY[candidate.matchQuality] >
      MATCH_QUALITY_PRIORITY[agg._bestQuality]
    ) {
      agg._bestQuality = candidate.matchQuality;
    }

    const hasOption =
      candidate.option !== null &&
      candidate.option !== undefined &&
      candidate.option !== '';
    const option = hasOption ? String(candidate.option) : UNPAIRED_OPTION_KEY;
    const optionKey = hasOption ? normalizeText(option) : UNPAIRED_OPTION_KEY;
    if (!agg._pairs.has(optionKey)) {
      agg._pairs.set(optionKey, {
        option,
        _amtIds: new Set(),
      });
    }
    if (
      candidate.amt !== null &&
      candidate.amt !== undefined &&
      candidate.amt !== ''
    ) {
      agg._pairs.get(optionKey)._amtIds.add(String(candidate.amt));
    }
  });

  return Array.from(byKey.values()).map((agg) => {
    const optionAmtPairs = Array.from(agg._pairs.values())
      .map((pair) => ({
        option: pair.option,
        amtIds: unique(Array.from(pair._amtIds)).sort((a, b) =>
          a.localeCompare(b)
        ),
      }))
      .sort((a, b) => {
        if (
          a.option === UNPAIRED_OPTION_KEY &&
          b.option !== UNPAIRED_OPTION_KEY
        ) {
          return 1;
        }
        if (
          b.option === UNPAIRED_OPTION_KEY &&
          a.option !== UNPAIRED_OPTION_KEY
        ) {
          return -1;
        }
        return a.option.localeCompare(b.option);
      });

    return {
      searchType: agg.searchType,
      page: agg.page,
      group: agg.group,
      name: agg.name,
      available: agg.available,
      optionAmtPairs,
      _bestQuality: agg._bestQuality,
    };
  });
};

const rankResults = (results) => {
  return [...results].sort((left, right) => {
    const availabilityDelta = Number(right.available) - Number(left.available);
    if (availabilityDelta !== 0) {
      return availabilityDelta;
    }

    const matchTypeDelta =
      MATCH_TYPE_PRIORITY[right.searchType] -
      MATCH_TYPE_PRIORITY[left.searchType];
    if (matchTypeDelta !== 0) {
      return matchTypeDelta;
    }

    const qualityDelta =
      MATCH_QUALITY_PRIORITY[right._bestQuality] -
      MATCH_QUALITY_PRIORITY[left._bestQuality];
    if (qualityDelta !== 0) {
      return qualityDelta;
    }

    const leftLabel = String(left.name || left.group || left.page || '');
    const rightLabel = String(right.name || right.group || right.page || '');
    return leftLabel.localeCompare(rightLabel);
  });
};

export const buildSearchResults = (
  query,
  options,
  amtIndex,
  svFamily = null
) => {
  const normalizedQuery = normalizeText(query);
  if (normalizedQuery.length < 2) return [];

  const treeSearchIndex = buildTreeSearchIndex(getTreeStructure(svFamily));

  const optionIndex = buildOptionIndex(options);
  const baseCandidates = buildBaseCandidates(
    normalizedQuery,
    treeSearchIndex,
    optionIndex,
    amtIndex
  );
  const resolvedCandidates = resolveCandidateAvailability(
    baseCandidates,
    optionIndex,
    amtIndex
  );
  const aggregated = aggregateCandidates(resolvedCandidates);
  const ranked = rankResults(aggregated);

  return ranked
    .map(({ _bestQuality, ...clean }) => clean)
    .slice(0, SEARCH_RESULT_LIMIT);
};
